import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Syncs document-level metadata (<html lang>, <title>, <meta>, OG tags)
 * whenever the i18next language changes.
 */
export const useDocumentMeta = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language;

    // 1. Update <html lang="...">
    document.documentElement.lang = lang;

    // 2. Update <title>
    document.title = t('meta.title');

    // 3. Helper to set or create a <meta> tag
    const setMeta = (selector: string, content: string, attr = 'name') => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        if (selector.includes('property=')) {
          el.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] ?? '');
        } else {
          el.setAttribute(attr, selector.match(/name="([^"]+)"/)?.[1] ?? '');
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // 4. Update meta description
    setMeta('meta[name="description"]', t('meta.description'));

    // 5. Update Open Graph tags
    setMeta('meta[property="og:title"]', t('meta.og_title'));
    setMeta('meta[property="og:description"]', t('meta.og_description'));
    setMeta('meta[property="og:locale"]', t('meta.og_locale'));
  }, [i18n.language, t]);
};
